export const PLATFORMS = ["terminus", "foundershub", "gaia"] as const;
export type Platform = (typeof PLATFORMS)[number];

/** The platforms that have founders. Terminus does not, so it has no B2C
 * portal and never appears in a `/entrepreneur/*` route or endpoint. Derived
 * from here rather than re-listed: a `Record<B2CPlatform, …>` elsewhere then
 * fails to compile when this list grows, instead of silently missing a case. */
export const B2C_PLATFORMS = ["foundershub", "gaia"] as const;
export type B2CPlatform = (typeof B2C_PLATFORMS)[number];

const PLATFORM_HOSTS: Record<Platform, string | undefined> = {
  terminus: import.meta.env.VITE_TERMINUS_HOST,
  foundershub: import.meta.env.VITE_FOUNDERSHUB_HOST,
  gaia: import.meta.env.VITE_GAIA_HOST,
};

const hostname = window.location.hostname;
const protocol = window.location.protocol;

/** The platform this host serves, or null when no host matches — local dev and
 * the bare domain, where the platform lives in the URL prefix instead. */
export const hostPlatform: Platform | null =
  PLATFORMS.find((p) => PLATFORM_HOSTS[p] && hostname === PLATFORM_HOSTS[p]) ?? null;

function platformFromPath(pathname: string): Platform | null {
  const first = pathname.split("/")[1] as Platform;
  return PLATFORMS.includes(first) ? first : null;
}

/** The platform the user is in: the host decides it, else the URL prefix. */
export function currentPlatform(): Platform | null {
  return hostPlatform ?? platformFromPath(window.location.pathname);
}

function currentPrefix(): string {
  if (hostPlatform) return "";
  const p = platformFromPath(window.location.pathname);
  return p ? `/${p}` : "";
}

/** Una ruta en la plataforma en la que YA estás: relativa en el host de un
 *  producto, prefijada en modo prefijo. El complemento de `platformPath`, que
 *  obliga a nombrar la plataforma. Las superficies que pertenecen a la
 *  plataforma actual — `/login`, el portal B2C, `/org`, `/superadmin` — se
 *  construyen con este, nunca con un literal absoluto. */
export function scopedPath(path: string): string {
  return `${currentPrefix()}${path}`;
}

/** Link to `path` inside `platform`, valid from wherever we are.
 *
 * On that platform's own host (and in prefix mode) it stays relative. On a
 * *different* platform's host it becomes an absolute URL on the right host —
 * never `gaia.vess.digital/terminus/...`: a platform prefix belongs to another
 * platform's host as much as its routes do, which is to say not at all. */
export function platformPath(platform: Platform, path: string): string {
  if (hostPlatform === platform) return path;
  if (hostPlatform) {
    const host = PLATFORM_HOSTS[platform];
    return host ? `${protocol}//${host}${path}` : path;
  }
  return `/${platform}${path}`;
}

export function tPath(path: string): string { return platformPath("terminus", path); }
export function fPath(path: string): string { return platformPath("foundershub", path); }
export function gPath(path: string): string { return platformPath("gaia", path); }

/** The B2C section of a platform. `/entrepreneurs` (plural) is the staff area
 * that manages them; `/entrepreneur` is the founders' own portal. */
const B2C_BASE = "/entrepreneur";

/** The B2C portal of a NAMED platform — for links that deliberately cross to
 * another one, e.g. the portal switcher. */
export function b2cPath(platform: Platform, path: string): string {
  return platformPath(platform, `${B2C_BASE}${path}`);
}

/** The B2C portal of the platform we are already in. */
export function ePath(path: string): string { return scopedPath(`${B2C_BASE}${path}`); }

/** Are we inside the B2C portal of whatever platform we are on?
 *
 * The match is segment-bounded on purpose: `/entrepreneurs` (plural) is the
 * staff area that manages founders, and a bare `startsWith` swallowed it —
 * `/foundershub/entrepreneurs` read as the portal, so staff there were
 * authenticated against `/entrepreneur/me` and role-filtered to ENTREPRENEUR. */
export function isEntrepreneurPath(pathname: string = window.location.pathname): boolean {
  const base = `${currentPrefix()}${B2C_BASE}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** The documentation of a platform, or the cross-platform docs root.
 *
 * The docs are one of those root-mounted surfaces: a product is a SECTION of
 * them, not a host or a prefix of its own, so `/docs/gaia/` is the same string
 * on `gaia.vess.digital` and in prefix mode. `/gaia/docs` also reaches it (the
 * server redirects), but only the section spelling is valid on both. */
export function docsPath(platform: Platform | null = currentPlatform()): string {
  return platform ? `/docs/${platform}/` : "/docs/";
}

/** El login de staff en el host en el que ya estás. En modo prefijo lleva la
 * plataforma, que es lo que decide qué marca lleva puesta. */
export function loginHref(): string { return scopedPath('/login'); }

/** Reject a `?redirect=` value that would send the browser off-origin
 * (absolute URL, protocol-relative `//host`, `javascript:`, etc.) — an
 * unvalidated redirect target is an open-redirect / phishing vector. */
export function resolveSameOriginRedirect(rawRedirect: string): string | null {
  try {
    const decoded = decodeURIComponent(rawRedirect);
    const resolved = new URL(decoded, window.location.origin);
    return resolved.origin === window.location.origin ? decoded : null;
  } catch {
    return null;
  }
}
